// src/components/admin/blog/Dashboard.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle, 
  AlertTriangle,
  Eye, 
  FolderOpen, 
  Tag as TagIcon,
  Plus,
  Edit,
  Calendar,
  TrendingUp
} from 'lucide-react';
import StatusBadge from '../shared/StatusBadge';

const Dashboard = ({ stats, onRefresh }) => {
  const navigate = useNavigate();
  
  // Function to format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Total Posts</h3>
              <p className="text-xl font-bold">{stats.totalPosts}</p>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg">
              <FileText className="h-6 w-6 text-blue-500" />
            </div>
          </div>
          <div className="mt-4">
            <button 
              onClick={() => navigate('/pitech/blog/posts/new')}
              className="text-blue-500 text-sm hover:text-blue-700 font-medium flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Create new post
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Published</h3>
              <p className="text-xl font-bold">{stats.publishedPosts}</p>
            </div>
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
          </div>
          <div className="mt-4">
            <button 
              onClick={() => navigate('/pitech/blog/posts?status=published')}
              className="text-green-500 text-sm hover:text-green-700 font-medium flex items-center"
            >
              <Eye className="h-4 w-4 mr-1" />
              View published posts
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-amber-500">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Drafts</h3>
              <p className="text-xl font-bold">{stats.draftPosts}</p>
            </div>
            <div className="p-2 bg-amber-50 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
            </div>
          </div>
          <div className="mt-4">
            <button 
              onClick={() => navigate('/pitech/blog/posts?status=draft')}
              className="text-amber-500 text-sm hover:text-amber-700 font-medium flex items-center"
            >
              <Edit className="h-4 w-4 mr-1" />
              Manage drafts
            </button>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Categories</h3>
              <p className="text-xl font-bold">{stats.categories}</p>
            </div>
            <div className="p-2 bg-gray-100 rounded-lg">
              <FolderOpen className="h-6 w-6 text-gray-600" />
            </div>
          </div>
          <div className="mt-4">
            <button 
              onClick={() => navigate('/pitech/blog/categories')}
              className="text-gray-600 text-sm hover:text-gray-800 font-medium flex items-center"
            >
              <FolderOpen className="h-4 w-4 mr-1" />
              Manage categories
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Tags</h3>
              <p className="text-xl font-bold">{stats.tags}</p>
            </div>
            <div className="p-2 bg-gray-100 rounded-lg">
              <TagIcon className="h-6 w-6 text-gray-600" />
            </div>
          </div>
          <div className="mt-4">
            <button 
              onClick={() => navigate('/pitech/blog/tags')}
              className="text-gray-600 text-sm hover:text-gray-800 font-medium flex items-center"
            >
              <TagIcon className="h-4 w-4 mr-1" />
              Manage tags
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Total Views</h3>
              <p className="text-xl font-bold">{stats.totalViews?.toLocaleString() || 0}</p>
            </div>
            <div className="p-2 bg-gray-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-gray-600" />
            </div>
          </div>
          <div className="mt-4">
            <button 
              onClick={() => navigate('/pitech/blog/posts?sort=popular')}
              className="text-gray-600 text-sm hover:text-gray-800 font-medium flex items-center"
            >
              <Eye className="h-4 w-4 mr-1" />
              View popular posts
            </button>
          </div>
        </div>
      </div>

      {/* Most viewed posts */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">Most Viewed Posts</h3>
        </div>
        <div className="p-4">
          {stats.mostViewedPosts && stats.mostViewedPosts.length > 0 ? (
            <div className="space-y-3">
              {stats.mostViewedPosts.map(post => (
                <div key={post._id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-md">
                  <div className="flex items-center">
                    <button 
                      onClick={() => navigate(`/pitech/blog/posts/edit/${post._id}`)}
                      className="font-normal text-left text-gray-800 hover:text-blue-600 truncate-title"
                      title={post.title}
                    >
                      {window.innerWidth <= 768 
                        ? (post.title.length > 100 
                            ? post.title.substring(0, 100) + '...' 
                            : post.title)
                        : post.title}
                    </button>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-sm flex items-center">
                      <Eye className="h-3 w-3 mr-1" />
                      {post.viewCount}
                    </span>
                    <button
                      onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/pitech/blog/posts/edit/${post._id}`)}
                      className="text-amber-500 hover:text-amber-700"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              No post data available
            </div>
          )}
        </div>
      </div>

      {/* Recent posts */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">Recent Posts</h3>
        </div>
        <div className="p-4">
          {stats.recentPosts && stats.recentPosts.length > 0 ? (
            <div className="space-y-3">
              {stats.recentPosts.map(post => (
                <div key={post._id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-md">
                  <div className="flex items-center">
                    <button 
                      onClick={() => navigate(`/pitech/blog/posts/edit/${post._id}`)}
                      className="font-normal text-gray-800 hover:text-blue-600 truncate-title text-left"
                      title={post.title}
                    >
                      {window.innerWidth <= 768 
                        ? (post.title.length > 100 
                            ? post.title.substring(0, 100) + '...' 
                            : post.title)
                        : post.title}
                    </button>
                  </div>
                  <div className="flex items-center space-x-4">
                    <StatusBadge status={post.status} />
                    <span className="text-xs text-gray-500 flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {formatDate(post.createdAt)}
                    </span>
                    <button
                      onClick={() => navigate(`/pitech/blog/posts/edit/${post._id}`)}
                      className="text-amber-500 hover:text-amber-700"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              No recent posts available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;